import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Select,
    MenuItem,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { authHeader, clearToken } from "../auth";
import { usePresencePing } from "../hooks/usePresencePing";

const expectedFields = [
    "rank",
    "bib",
    "fiscode",
    "name",
    "birthyear",
    "nation",
    "skiTime",
    "penalty",
    "totalTime",
    "gap",
    "transponder",
];

const STORAGE_KEY = "qualificationsMapping";

export default function QualificationsImportPage() {
    const navigate = useNavigate();
    const { category } = useParams();
    usePresencePing(category, "Import qualifications");

    const [rows2D, setRows2D] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [mapping, setMapping] = useState({});
    const [ignoreRows, setIgnoreRows] = useState(0);
    const [mappedData, setMappedData] = useState([]);

    const [importedQualifs, setImportedQualifs] = useState([]);

    // -----------------------------
    // API FETCH AVEC TOKEN (authHeader())
    // -----------------------------
    const apiFetch = useCallback(
        async (url, options = {}) => {
            const res = await fetch(url, {
                ...options,
                headers: {
                    "Content-Type": "application/json",
                    ...authHeader(),
                    ...(options.headers || {}),
                },
            });

            if (res.status === 401) {
                clearToken();
                navigate("/login");
                return null;
            }

            return res;
        },
        [navigate]
    );

    // -----------------------------
    // Charger le mapping sauvegardé
    // -----------------------------
    useEffect(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const { ignoreRows: savedIgnore, mapping: savedMapping } = JSON.parse(saved);
            setIgnoreRows(savedIgnore ?? 0);
            setMapping(savedMapping ?? {});
        }
    }, []);

    // -----------------------------
    // 1. Lire le fichier Excel brut
    // -----------------------------
    const handleFileSelect = async (file) => {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

        setRows2D(rows);
        setHeaders(rows[ignoreRows] || []);
        setMappedData([]);
    };

    // -----------------------------
    // 2. Recalcul des headers selon ignoreRows
    // -----------------------------
    useEffect(() => {
        if (rows2D.length > 0) {
            setHeaders(rows2D[ignoreRows] || []);
        }
    }, [ignoreRows, rows2D]);

    // -----------------------------
    // 3. Appliquer le mapping
    // -----------------------------
    const applyMapping = () => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ignoreRows, mapping }));

        if (rows2D.length === 0) return;

        const headerRow = rows2D[ignoreRows];
        const dataRows = rows2D.slice(ignoreRows + 1);

        const mapped = dataRows.map((row, index) => {
            const obj = {};

            expectedFields.forEach((field) => {
                const colName = mapping[field];
                const colIndex = headerRow.indexOf(colName);

                let value = colIndex >= 0 ? row[colIndex] : null;
                if (value === "") value = null;

                obj[field] = value;
            });

            obj.rank = parseInt(obj.rank) || index + 1;
            obj.skiTime = parseFloat(obj.skiTime) || 0;
            obj.penalty = parseFloat(obj.penalty) || 0;
            obj.totalTime = parseFloat(obj.totalTime) || 0;
            obj.gap = parseFloat(obj.gap) || 0;

            return obj;
        });

        setMappedData(mapped);
    };

    // -----------------------------
    // 4. Envoyer au backend
    // -----------------------------
    const sendToBackend = async () => {
        if (mappedData.length === 0) return;

        const res = await apiFetch(`/api/qualifications/import/${category}`, {
            method: "POST",
            body: JSON.stringify(mappedData),
        });

        if (!res) return;

        const data = await res.json();
        setImportedQualifs(data[category] || []);
        navigate("/parallel");
    };

    // -----------------------------
    // Rendu
    // -----------------------------
    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>
                Import qualifications (Excel) — {category}
            </Typography>

            {/* 1. Sélection du fichier */}
            <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    handleFileSelect(file);
                }}
            />

            {/* 2. Mapping */}
            {rows2D.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6">Mapping des colonnes</Typography>

                    <Box sx={{ mt: 2, mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
                        <Typography variant="subtitle1">
                            Ligne des en-têtes (ignorer les lignes avant)
                        </Typography>

                        <Select
                            value={ignoreRows}
                            onChange={(e) => setIgnoreRows(Number(e.target.value))}
                            size="small"
                        >
                            {[0, 1, 2, 3, 4, 5].map((n) => (
                                <MenuItem key={n} value={n}>
                                    {n}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Champ attendu (backend)</TableCell>
                                <TableCell>Colonne Excel</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {expectedFields.map((field) => (
                                <TableRow key={field}>
                                    <TableCell>{field}</TableCell>
                                    <TableCell>
                                        <Select
                                            value={mapping[field] || ""}
                                            onChange={(e) =>
                                                setMapping((prev) => ({
                                                    ...prev,
                                                    [field]: e.target.value,
                                                }))
                                            }
                                            fullWidth
                                            displayEmpty
                                        >
                                            <MenuItem value="">
                                                <em>Non utilisé</em>
                                            </MenuItem>

                                            {headers.map((h) => (
                                                <MenuItem key={h} value={h}>
                                                    {h}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Button variant="contained" sx={{ mt: 2 }} onClick={applyMapping}>
                        Prévisualiser
                    </Button>
                </Box>
            )}

            {/* 3. Prévisualisation */}
            {mappedData.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6">Prévisualisation</Typography>

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Rang</TableCell>
                                <TableCell>Dossard</TableCell>
                                <TableCell>Nom</TableCell>
                                <TableCell>Ski Time</TableCell>
                                <TableCell>Penalty</TableCell>
                                <TableCell>Total</TableCell>
                                <TableCell>Gap</TableCell>
                                <TableCell>Transponder</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {mappedData.slice(0, 10).map((r, i) => (
                                <TableRow key={i}>
                                    <TableCell>{r.rank}</TableCell>
                                    <TableCell>{r.bib}</TableCell>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell>{r.skiTime}</TableCell>
                                    <TableCell>{r.penalty}</TableCell>
                                    <TableCell>{r.totalTime}</TableCell>
                                    <TableCell>{r.gap}</TableCell>
                                    <TableCell>{r.transponder}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Button variant="contained" sx={{ mt: 2 }} onClick={sendToBackend}>
                        Envoyer au backend
                    </Button>
                </Box>
            )}

            {/* 4. Tableau final */}
            {importedQualifs.length > 0 && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6">Résultats importés</Typography>

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Rang</TableCell>
                                <TableCell>Dossard</TableCell>
                                <TableCell>Nom</TableCell>
                                <TableCell>Ski Time</TableCell>
                                <TableCell>Penalty</TableCell>
                                <TableCell>Total</TableCell>
                                <TableCell>Gap</TableCell>
                                <TableCell>Transponder</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {importedQualifs.map((r, i) => (
                                <TableRow key={i}>
                                    <TableCell>{r.rank}</TableCell>
                                    <TableCell>{r.bib}</TableCell>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell>{r.skiTime}</TableCell>
                                    <TableCell>{r.penalty}</TableCell>
                                    <TableCell>{r.totalTime}</TableCell>
                                    <TableCell>{r.gap}</TableCell>
                                    <TableCell>{r.transponder}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </Box>
    );
}